import React, { useEffect, useState } from 'react';
import { Box, Typography, Grid, Button, Card, CardHeader, CardContent, CardActions, IconButton, TextField } from '@mui/material';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { stateListAtom, userInformationAtom } from '../../Services/_states';
import { ConstantActions } from '../../Services/_actions';
import { OrganisationActions } from '../../Services/_actions/Organisation/organisation.actions';
import OrganisationDropdown from '../Shared/OrganisationDropdown/index.tsx';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useSnackBar } from '../FlowBuilder/Common/SnackBar/index.tsx';
import ModalForms from '../Shared/Modals/index.js';
import SearchableDropdown from '../FlowBuilder/Common/SearchableDropdown/SearchableDropdown.tsx';

const UpdateAddress1 = () => {
    const actions = ConstantActions();
    const organisationActions = OrganisationActions();
    const loggedInUser = useRecoilValue(userInformationAtom);
    const [organisationList, setOrganisationList] = useState([]);
    const stateList = useRecoilValue(stateListAtom);
    const setStateList = useSetRecoilState(stateListAtom);
    const [cityList, setCityList] = useState([]);
    const [areaList, setAreaList] = useState([]);
    const [addressList, setAddressList] = useState([]);
    const [isModalShow, setIsModalShow] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [isAddFetched, setIsAddFetched] = useState(false);
    const [selectedOrg, setSelectedOrg] = useState(null);
    const { showSnackBar } = useSnackBar();

    const [formData, setFormData] = useState({
        company_id: '',
        state_id: '',
        city_id: '',
        area: '',
        address: '',
        pincode: '',
    });

    useEffect(() => {
        const fetchStates = async () => {
            const res = await actions.getStateList();
            if (res?.status === 200 && Array.isArray(res.data)) {
                setStateList(res.data);
            } else {
                console.error('Failed to fetch state list');
            }
        };

        fetchStates();
    }, []);

    useEffect(() => {
        const loadOrganisationsAndStates = async () => {
            if (loggedInUser?.is_superuser) {
                let res = await actions.getOrganisations();
                setOrganisationList(res || []);
            } else {
                setFormData({
                    ...formData,
                    company_id: loggedInUser?.profile?.organisation?.organisation_id,
                });
                getCompanyAddress(loggedInUser?.profile?.organisation);
            }
            await actions.getStateList();
        };
        loadOrganisationsAndStates();
    }, []);

    const getCompanyAddress = async (e) => {
        setFormData({ ...formData, company_id: e?.organisation_id || '' });
        if (!e?.organisation_id) {
            setIsAddFetched(false);
            return;
        }
        const res = await organisationActions.getAddress(e?.organisation_id);
        if (res.status === 200) {
            setAddressList(res?.data);
            setIsAddFetched(true);
        } else {
            showSnackBar('An error occurred while fetching address.', 'error');
        }
    };

    const onSelectOrganisation = (selectedOrg) => {
        setSelectedOrg(selectedOrg);
        setFormData((prev) => ({
            ...prev,
            company_id: selectedOrg?.organisation_id || '',
        }));

        if (selectedOrg?.organisation_id) {
            getCompanyAddress({ organisation_id: selectedOrg.organisation_id });
        }
    };

    const handleEditAddress = async (data) => {
        setFormData({ ...data });
        setIsModalShow(true);
        setIsEditMode(true);

        try {
            let stateId = stateList?.find((item) => item?.state_code === data?.state_id)?.id;
            if (stateId) {
                const cities = await actions.getCityByState(stateId);
                setCityList(cities || []);
            }
            const areas = await actions.getAreaByCity(data?.city_id);
            setAreaList(areas || []);
        } catch (error) {
            setAreaList([]);
            setCityList([]);
        }
    };

    const handleDeleteAddress = async (data) => {
        try {
            const res = await organisationActions.deleteAddress(data);
            if (res?.status === 200) {
                setAddressList(addressList?.filter((detail) => detail.address_id !== data?.address_id));
                showSnackBar('Address delete successfully.', 'success');
            } else
                showSnackBar('Failed to delete address. Please try again.', 'error');
        } catch (error) {
            showSnackBar('An error occurred while deleting the address details.', 'error');
        }
    }

    const handleStateChange = async (e) => {
        setFormData({ ...formData, state_id: e?.state_code || '' });
        if (e?.id) {
            const cities = await actions.getCityByState(e?.id);
            setCityList(cities || []);
        } else {
            setCityList([]);
        }
    };

    const handleCityChange = async (selectedCity) => {
        setFormData({ ...formData, city_id: selectedCity?.id || '' });
        if (selectedCity?.id) {
            try {
                const areas = await actions.getAreaByCity(selectedCity?.id);
                setAreaList(areas || []);
            } catch (error) {
                setAreaList([]);
            }
        } else {
            setAreaList([]);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const missingFields = [];
        if (!formData.company_id) missingFields.push('Company');
        if (!formData.state_id) missingFields.push('State');
        if (!formData.city_id) missingFields.push('City');
        if (!formData.area) missingFields.push('Area');

        if (missingFields.length > 0) {
            showSnackBar(`Please fill in the following fields: ${missingFields.join(', ')}`, 'error');
            return;
        }

        try {
            const res = await organisationActions.createAddress(formData);
            if (res.status === 200) {
                showSnackBar('Address Added successfully.', 'success');
                setIsModalShow(false);
                if (formData?.address_id) {
                    setAddressList((prevAddresses) =>
                        prevAddresses.map((address) =>
                            address.address_id === formData?.address_id ? { ...address, ...formData } : address
                        )
                    );
                } else {
                    getCompanyAddress({ organisation_id: formData?.company_id });
                }
                setFormData({
                    ...formData,
                    state_id: '',
                    city_id: '',
                    area: '',
                    address: '',
                    pincode: '',
                });
            } else {
                showSnackBar('Failed to add address. Please try again.', 'error');
            }
        } catch (error) {
            showSnackBar('An error occurred while submitting the address details.', 'error');
        }
    };

    return (
        <Box>
            <Typography variant="h6" component="h1" sx={{ fontWeight: 'bold', color: '#333', mb: 2 }}>
                Update Address
            </Typography>
            <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <Grid item xs={12} md={6}>
                    {loggedInUser?.is_superuser && (
                        <Box sx={{ maxWidth: 210 }}>
                            <OrganisationDropdown
                                value={selectedOrg}
                                onChange={onSelectOrganisation}
                                label="Select Company"
                            />
                        </Box>
                    )}
                </Grid>
                <Grid item xs={12} md={6} sx={{ textAlign: 'right' }}>
                    {formData?.company_id && (
                        <Button
                            size='small'
                            variant="contained"
                            onClick={() => {
                                setIsModalShow(true);
                                setIsEditMode(false);
                                setFormData(
                                    Object.fromEntries(
                                        Object.entries(formData).map(([key, value]) => [
                                            key,
                                            key === 'company_id' ? value : '',
                                        ])
                                    )
                                );
                            }}
                        >
                            Add
                        </Button>
                    )}
                </Grid>
            </Grid>
            {isAddFetched && (
                <Grid container spacing={3}>
                    {addressList?.length > 0 ? (
                        addressList.map((address, index) => (
                            <Grid item xs={12} sm={6} md={3} key={index}>
                                <Card sx={{ height: '100%' }}>
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom>
                                            Address {index + 1}
                                        </Typography>
                                        <Typography variant="body2">
                                            <strong>Company ID:</strong> {address?.company_id}
                                            <br />
                                            <strong>State:</strong> {address?.state_id}
                                            <br />
                                            <strong>City:</strong> {address?.city_id}
                                            <br />
                                            <strong>Area:</strong> {address?.area}
                                            <br />
                                            <strong>Address:</strong> {address?.address}
                                            <br />
                                            <strong>Pincode:</strong> {address?.pincode}
                                        </Typography>
                                    </CardContent>
                                    <CardActions sx={{ justifyContent: 'flex-end' }}>
                                        <IconButton color="primary" onClick={() => handleEditAddress(address)} size="small">
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton color="error" onClick={() => handleDeleteAddress(address)} size="small">
                                            <DeleteIcon />
                                        </IconButton>
                                    </CardActions>
                                </Card>
                            </Grid>
                        ))
                    ) : (
                        <Grid item xs={12}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    minHeight: '150px',
                                    width: '100%',
                                }}
                            >
                                <Typography variant="body1">No address found.</Typography>
                            </Box>
                        </Grid>
                    )}
                </Grid>
            )}
            {isModalShow && (
                <ModalForms
                    show={isModalShow}
                    handleClose={() => setIsModalShow(false)}
                    modalHeader={`${isEditMode ? 'Edit' : 'Create'} Address Details`}
                    modalId={'address-modal'}
                    size="md"
                    modalBody={
                        <form onSubmit={handleSubmit}>
                            <Box gap={2} sx={{ display: { xs: 'flex' }, flexDirection: { xs: 'column', sm: 'row' }, mb: { xs: 2, sm: 3 }, width: { xs: '100%', sm: 'auto' } }}>
                                <Box flex={1}>
                                    <Typography>Select State</Typography>
                                    <SearchableDropdown
                                        options={stateList || []}
                                        labelKey="state_name"
                                        valueKey="state_code"
                                        label="Select State"
                                        value={
                                            stateList?.find((item) => item?.state_code === formData?.state_id) || null
                                        }
                                        onChange={(selected) => handleStateChange(selected)}
                                        disabled={isEditMode}
                                        size="small"
                                        sx={{ mt: "5px" }}
                                    />
                                </Box>
                                <Box flex={1}>
                                    <Typography>Select City</Typography>
                                    <SearchableDropdown
                                        options={cityList || []}
                                        labelKey="city_name"
                                        valueKey="id"
                                        label="Select City"
                                        value={
                                            cityList?.find((item) => item?.id === formData?.city_id) || null
                                        }
                                        onChange={(selected) => handleCityChange(selected)}
                                        disabled={isEditMode}
                                        size="small"
                                        sx={{ mt: "5px" }}
                                    />
                                </Box>
                            </Box>

                            <Box display="flex" gap={2} sx={{ mb: { xs: 1, sm: 0 }, flexDirection: { xs: 'column', sm: 'row' } }}>
                                <Box flex={1}>
                                    <Typography>Select Area</Typography>
                                    <SearchableDropdown
                                        options={areaList || []}
                                        labelKey="label"
                                        valueKey="area_code"
                                        label="Select Area"
                                        value={
                                            areaList?.find((item) => item?.area_code === formData?.area) || null
                                        }
                                        onChange={(selected) =>
                                            setFormData({ ...formData, area: selected?.area_code || '' })
                                        }
                                        size="small"
                                        sx={{ mt: "5px" }}
                                    />
                                </Box>
                                <Box display="flex" gap={2} mb={3}>
                                    <Box flex={1}>
                                        <Typography>Address</Typography>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            sx={{ mt: "10px" }}
                                            type="text"
                                            required
                                            value={formData?.address || ''}
                                            onChange={(e) =>
                                                setFormData({ ...formData, address: e.target.value })
                                            }
                                        />
                                    </Box>
                                </Box>
                            </Box>

                            <Box flex={1} sx={{ mb: 2 }}>
                                <Typography>Pincode</Typography>
                                <TextField
                                    fullWidth
                                    size="small"
                                    sx={{ mt: "5px" }}
                                    type="text"
                                    required
                                    value={formData?.pincode || ''}
                                    onChange={(e) =>
                                        setFormData({ ...formData, pincode: e.target.value })
                                    }
                                    inputProps={{
                                        maxLength: 6,
                                        pattern: '[0-9]{6}',
                                    }}
                                    helperText="Please enter a valid 6-digit pincode"
                                    FormHelperTextProps={{
                                        sx: { ml: "2px" }
                                    }}
                                />
                            </Box>

                            <Button size='small' type="submit" variant="contained" color="primary">
                                Submit
                            </Button>
                        </form>
                    }
                />
            )}
        </Box>
    )
};

export default UpdateAddress1;